import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Mail, Phone, Calendar, MessageSquare, Search, X,
  ChevronLeft, ChevronRight, Download, LayoutGrid, List,
  Inbox, Clock, AtSign,
} from 'lucide-react';
import API from '../../lib/api';

const PAGE_SIZE = 12;

const SORT_OPTIONS = [
  { value: 'newest',   label: 'Newest first' },
  { value: 'oldest',   label: 'Oldest first' },
  { value: 'name_asc', label: 'Name A → Z' },
  { value: 'name_desc',label: 'Name Z → A' },
];

const RANGE_OPTIONS = [
  { value: '',     label: 'All time'    },
  { value: '24h',  label: 'Last 24 hrs' },
  { value: '7d',   label: 'Last 7 days' },
  { value: '30d',  label: 'Last 30 days'},
  { value: '90d',  label: 'Last 90 days'},
];

function withinRange(createdAt, range) {
  if (!range) return true;
  const ms = { '24h': 864e5, '7d': 7*864e5, '30d': 30*864e5, '90d': 90*864e5 }[range];
  return Date.now() - new Date(createdAt).getTime() <= ms;
}

export default function ContactsPanel() {
  const [contacts, setContacts] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');

  const [filters, setFilters] = useState({
    search: '',
    range: '',
    has_phone: '',
    sort: 'newest',
  });
  const [view, setView] = useState('list'); // 'list' | 'grid'
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await API.get('/admin/contacts', { params: { page: 1, limit: 500 } });
      setContacts(data.contacts || []);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Failed to load contacts');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleFilter = (key, val) => {
    setFilters((f) => ({ ...f, [key]: val }));
    setPage(1);
  };
  const handleReset = () => {
    setFilters({ search: '', range: '', has_phone: '', sort: 'newest' });
    setPage(1);
  };

  const filtered = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    let arr = contacts.filter((c) => {
      if (q) {
        const hay = `${c.name || ''} ${c.email || ''} ${c.phone || ''} ${c.message || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (filters.has_phone === 'yes' && !c.phone) return false;
      if (filters.has_phone === 'no'  &&  c.phone) return false;
      if (!withinRange(c.created_at, filters.range)) return false;
      return true;
    });

    arr.sort((a, b) => {
      switch (filters.sort) {
        case 'oldest':    return new Date(a.created_at) - new Date(b.created_at);
        case 'name_asc':  return (a.name || '').localeCompare(b.name || '');
        case 'name_desc': return (b.name || '').localeCompare(a.name || '');
        default:          return new Date(b.created_at) - new Date(a.created_at);
      }
    });
    return arr;
  }, [contacts, filters]);

  const stats = useMemo(() => {
    const day = 864e5;
    const now = Date.now();
    return {
      total:    contacts.length,
      last24h:  contacts.filter((c) => now - new Date(c.created_at).getTime() <= day).length,
      last7d:   contacts.filter((c) => now - new Date(c.created_at).getTime() <= 7 * day).length,
      withPhone:contacts.filter((c) => !!c.phone).length,
    };
  }, [contacts]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages);
  const visible    = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const exportCsv = () => {
    const header = ['ID', 'Name', 'Email', 'Phone', 'Message', 'Submitted'];
    const rows = filtered.map((c) => [
      c.id, c.name, c.email, c.phone || '', (c.message || '').replace(/\s+/g, ' '),
      new Date(c.created_at).toISOString(),
    ]);
    const csv = [header, ...rows]
      .map((r) => r.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = 'contact-submissions.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5">
      {/* Hero header */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#7C2D92] via-[#9333EA] to-[#C026D3] text-white px-6 py-6">
        <div className="absolute -top-20 -right-12 w-60 h-60 bg-white/15 rounded-full blur-3xl" />
        <div className="absolute -bottom-24 -left-12 w-60 h-60 bg-[#F472B6]/25 rounded-full blur-3xl" />
        <div className="relative flex items-end justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/15 backdrop-blur-sm border border-white/15 flex items-center justify-center">
              <Inbox className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-heading font-bold leading-tight">
                Contact Submissions
              </h1>
              <p className="text-sm text-white/75 font-body mt-0.5">
                Inbound leads from the public contact form
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 sm:gap-5">
            <div>
              <p className="text-[10px] font-body uppercase tracking-[0.18em] text-white/60">Total</p>
              <p className="text-xl font-heading font-bold mt-0.5">{stats.total}</p>
            </div>
            <div className="h-9 w-px bg-white/15" />
            <div>
              <p className="text-[10px] font-body uppercase tracking-[0.18em] text-white/60">Last 24h</p>
              <p className="text-xl font-heading font-bold mt-0.5">{stats.last24h}</p>
            </div>
            <div className="h-9 w-px bg-white/15" />
            <div>
              <p className="text-[10px] font-body uppercase tracking-[0.18em] text-white/60">Last 7d</p>
              <p className="text-xl font-heading font-bold mt-0.5">{stats.last7d}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total Submissions" value={stats.total}     icon={Inbox}     tone="text-[#6C63FF]" />
        <StatCard label="Last 24 Hours"     value={stats.last24h}   icon={Clock}     tone="text-amber-600" />
        <StatCard label="Last 7 Days"       value={stats.last7d}    icon={Calendar}  tone="text-sky-600" />
        <StatCard label="With Phone"        value={stats.withPhone} icon={Phone}     tone="text-green-600" />
      </div>

      {/* Filter bar */}
      <div className="bg-white rounded-xl border border-[#E8E6F8] p-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#9CA3AF]" />
          <input
            type="text"
            placeholder="Search name, email, phone, message…"
            value={filters.search}
            onChange={(e) => handleFilter('search', e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm font-body border border-[#E8E6F8] rounded-lg bg-[#F5F4FB] text-[#1A1A2E] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-1 focus:ring-[#6C63FF]/50"
          />
        </div>

        <select
          value={filters.range}
          onChange={(e) => handleFilter('range', e.target.value)}
          className="text-sm font-body border border-[#E8E6F8] rounded-lg px-3 py-2 bg-[#F5F4FB] text-[#1A1A2E] focus:outline-none focus:ring-1 focus:ring-[#6C63FF]/50"
        >
          {RANGE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>

        <select
          value={filters.has_phone}
          onChange={(e) => handleFilter('has_phone', e.target.value)}
          className="text-sm font-body border border-[#E8E6F8] rounded-lg px-3 py-2 bg-[#F5F4FB] text-[#1A1A2E] focus:outline-none focus:ring-1 focus:ring-[#6C63FF]/50"
        >
          <option value="">Any Phone</option>
          <option value="yes">Has Phone</option>
          <option value="no">No Phone</option>
        </select>

        <select
          value={filters.sort}
          onChange={(e) => handleFilter('sort', e.target.value)}
          className="text-sm font-body border border-[#E8E6F8] rounded-lg px-3 py-2 bg-[#F5F4FB] text-[#1A1A2E] focus:outline-none focus:ring-1 focus:ring-[#6C63FF]/50"
        >
          {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>

        <div className="inline-flex border border-[#E8E6F8] rounded-lg overflow-hidden">
          <button
            onClick={() => setView('list')}
            className={`flex items-center gap-1 px-3 py-2 text-xs font-body transition-colors ${
              view === 'list' ? 'bg-[#6C63FF] text-white' : 'bg-white text-[#6B7280] hover:bg-[#F5F4FB]'
            }`}
            title="List view"
          >
            <List className="h-3.5 w-3.5" /> List
          </button>
          <button
            onClick={() => setView('grid')}
            className={`flex items-center gap-1 px-3 py-2 text-xs font-body transition-colors ${
              view === 'grid' ? 'bg-[#6C63FF] text-white' : 'bg-white text-[#6B7280] hover:bg-[#F5F4FB]'
            }`}
            title="Grid view"
          >
            <LayoutGrid className="h-3.5 w-3.5" /> Grid
          </button>
        </div>

        <button
          onClick={exportCsv}
          disabled={filtered.length === 0}
          className="flex items-center gap-1.5 text-xs font-body text-[#6C63FF] border border-[#6C63FF]/30 hover:bg-[#6C63FF]/10 px-3 py-2 rounded-lg disabled:opacity-40 transition-colors"
        >
          <Download className="h-3.5 w-3.5" />
          Export CSV
        </button>

        <button
          onClick={handleReset}
          className="flex items-center gap-1.5 text-xs font-body text-[#6B7280] hover:text-[#1A1A2E] px-3 py-2 border border-[#E8E6F8] rounded-lg hover:bg-[#F5F4FB] transition-colors"
        >
          <X className="h-3 w-3" />
          Reset
        </button>
      </div>

      {/* Result counter */}
      <div className="flex items-center justify-between text-xs font-body text-[#6B7280]">
        <span>
          {loading ? 'Loading…' : `${filtered.length} of ${contacts.length} submissions`}
        </span>
      </div>

      {/* Body */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-7 h-7 border-2 border-[#6C63FF] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : error ? (
        <div className="bg-white rounded-2xl border border-[#E8E6F8] p-6 text-center">
          <p className="text-sm text-red-500 font-body">{error}</p>
          <button onClick={load} className="mt-2 text-xs text-[#6C63FF] underline font-body">Retry</button>
        </div>
      ) : visible.length === 0 ? (
        <EmptyState hasFilters={Boolean(filters.search || filters.range || filters.has_phone)} />
      ) : view === 'list' ? (
        <ListView rows={visible} expanded={expanded} setExpanded={setExpanded} />
      ) : (
        <GridView rows={visible} expanded={expanded} setExpanded={setExpanded} />
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-[#6B7280] font-body">
            Showing {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filtered.length)} of {filtered.length}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage === 1}
              className="flex items-center gap-1 text-xs font-body px-3 py-1.5 border border-[#E8E6F8] rounded-lg hover:bg-[#F5F4FB] disabled:opacity-40"
            >
              <ChevronLeft className="h-3.5 w-3.5" /> Prev
            </button>
            <span className="text-xs font-body text-[#6B7280]">Page {safePage} / {totalPages}</span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage === totalPages}
              className="flex items-center gap-1 text-xs font-body px-3 py-1.5 border border-[#E8E6F8] rounded-lg hover:bg-[#F5F4FB] disabled:opacity-40"
            >
              Next <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, tone }) {
  return (
    <div className="bg-white rounded-xl border border-[#E8E6F8] p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg bg-[#F5F4FB] flex items-center justify-center ${tone}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <p className="text-[10px] font-body uppercase tracking-wide text-[#9CA3AF]">{label}</p>
        <p className="text-xl font-heading font-bold text-[#1A1A2E] leading-tight">{value}</p>
      </div>
    </div>
  );
}

function EmptyState({ hasFilters }) {
  return (
    <div className="bg-white rounded-2xl border border-[#E8E6F8] p-12 text-center">
      <div className="w-12 h-12 rounded-full bg-[#F5F4FB] flex items-center justify-center mx-auto mb-3">
        <MessageSquare className="h-5 w-5 text-[#9CA3AF]" />
      </div>
      <p className="text-sm font-heading font-semibold text-[#1A1A2E]">
        {hasFilters ? 'No submissions match your filters' : 'No contact submissions yet'}
      </p>
      <p className="text-xs text-[#9CA3AF] font-body mt-1">
        {hasFilters ? 'Try clearing filters or expanding the date range.' : 'Submissions from the website contact form will appear here.'}
      </p>
    </div>
  );
}

function Avatar({ name, size = 'md' }) {
  const initial = name?.[0]?.toUpperCase() || '?';
  const cls = size === 'lg' ? 'w-11 h-11 text-base' : 'w-9 h-9 text-sm';
  return (
    <div className={`${cls} rounded-full bg-gradient-to-br from-[#6C63FF] to-[#8B7CFF] flex items-center justify-center text-white font-bold font-heading flex-shrink-0`}>
      {initial}
    </div>
  );
}

function ListView({ rows, expanded, setExpanded }) {
  return (
    <div className="bg-white rounded-2xl border border-[#E8E6F8] shadow-sm overflow-hidden">
      <div className="divide-y divide-[#E8E6F8]">
        {rows.map((c) => {
          const isOpen = expanded === c.id;
          const long   = (c.message || '').length > 140;
          return (
            <div key={c.id} className="p-5 hover:bg-[#FAFAFE] transition-colors">
              <div className="flex items-start gap-3">
                <Avatar name={c.name} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="text-sm font-heading font-bold text-[#1A1A2E]">{c.name}</p>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs font-body text-[#6B7280]">
                        <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1 hover:text-[#6C63FF] hover:underline">
                          <Mail className="h-3 w-3" /> {c.email}
                        </a>
                        {c.phone && (
                          <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1 hover:text-[#6C63FF] hover:underline">
                            <Phone className="h-3 w-3" /> {c.phone}
                          </a>
                        )}
                      </div>
                    </div>
                    <p className="text-[10px] text-[#9CA3AF] font-body whitespace-nowrap inline-flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {new Date(c.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                    </p>
                  </div>

                  <p className={`text-sm text-[#4A4A60] font-body mt-3 bg-[#F5F4FB] p-3 rounded-lg whitespace-pre-wrap ${isOpen ? '' : 'line-clamp-3'}`}>
                    {c.message}
                  </p>
                  {long && (
                    <button
                      onClick={() => setExpanded(isOpen ? null : c.id)}
                      className="text-xs text-[#6C63FF] font-body mt-1 hover:underline"
                    >
                      {isOpen ? 'Show less' : 'Show more'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function GridView({ rows, expanded, setExpanded }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {rows.map((c) => {
        const isOpen = expanded === c.id;
        const long   = (c.message || '').length > 120;
        return (
          <div key={c.id} className="bg-white rounded-2xl border border-[#E8E6F8] shadow-sm p-4 flex flex-col gap-3 hover:shadow-md transition-shadow">
            <div className="flex items-start gap-3">
              <Avatar name={c.name} size="lg" />
              <div className="min-w-0 flex-1">
                <p className="font-heading font-bold text-[#1A1A2E] truncate">{c.name}</p>
                <p className="text-[10px] text-[#9CA3AF] font-body inline-flex items-center gap-1 mt-0.5">
                  <Calendar className="h-3 w-3" />
                  {new Date(c.created_at).toLocaleDateString('en-IN', { dateStyle: 'medium' })}
                  <span>·</span>
                  {new Date(c.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </div>

            <div className="space-y-1.5 text-xs font-body text-[#6B7280] border-t border-[#F0EEF9] pt-3">
              <a href={`mailto:${c.email}`} className="flex items-center gap-1.5 truncate hover:text-[#6C63FF]">
                <AtSign className="h-3 w-3 flex-shrink-0" />
                <span className="truncate">{c.email}</span>
              </a>
              {c.phone ? (
                <a href={`tel:${c.phone}`} className="flex items-center gap-1.5 hover:text-[#6C63FF]">
                  <Phone className="h-3 w-3 flex-shrink-0" />
                  <span>{c.phone}</span>
                </a>
              ) : (
                <p className="flex items-center gap-1.5 text-[#9CA3AF]">
                  <Phone className="h-3 w-3 flex-shrink-0" />
                  <span>No phone</span>
                </p>
              )}
            </div>

            <div>
              <p className={`text-sm text-[#4A4A60] font-body bg-[#F5F4FB] p-3 rounded-lg whitespace-pre-wrap ${isOpen ? '' : 'line-clamp-4'}`}>
                {c.message}
              </p>
              {long && (
                <button
                  onClick={() => setExpanded(isOpen ? null : c.id)}
                  className="text-xs text-[#6C63FF] font-body mt-1 hover:underline"
                >
                  {isOpen ? 'Show less' : 'Show more'}
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
